<script setup lang="ts">
import { computed } from 'vue'
import { storeToRefs } from 'pinia'

import ImockupFloatingToolbar from '@/components/imockup/ImockupFloatingToolbar.vue'
import ImockupLightingPresets from '@/components/imockup/ImockupLightingPresets.vue'
import ImockupThreeViewport from '@/components/imockup/ImockupThreeViewport.vue'
import { useMockupEditorStore } from '@/stores/mockupEditor'

const store = useMockupEditorStore()
const { sceneWidth, sceneHeight, isModelLoading, modelLoadProgress } = storeToRefs(store)
const modelLoadProgressPct = computed(() => `${Math.round(Math.min(1, Math.max(0, modelLoadProgress.value)) * 100)}%`)

/**
 * 在 `.canvasFit` 可用矩形内按场景比例「完整套入」：用容器查询算宽度，避免 `width:auto` 在仅含绝对定位子元素时塌成 0。
 *
 * @see https://drafts.csswg.org/css-conditional-5/#container-lengths
 */
const canvasBoxStyle = computed(() => {
  const sw = Math.max(1, Number(sceneWidth.value) || 1)
  const sh = Math.max(1, Number(sceneHeight.value) || 1)
  const ar = sw / sh
  return {
    aspectRatio: `${sw} / ${sh}`,
    /** 宽取「撑满宽」与「以高为限按比例所需宽」的较小者，高由 aspect-ratio 推出，从而始终落在可视区内 */
    width: `min(100cqw, calc(100cqh * ${ar}))`,
    height: 'auto',
    maxWidth: '100%',
    maxHeight: '100%',
  } as const
})
</script>

<template>
  <section class="stage" aria-label="Animation canvas">
    <div class="canvasFit">
      <div class="canvas" :style="canvasBoxStyle">
        <ImockupThreeViewport />
        <div v-if="isModelLoading" class="loadingOverlay">
          <div class="loadingCard">
            <p class="loadingTitle">模型加载中</p>
            <div class="loadingBarWrap">
              <span class="loadingBar" :style="{ width: modelLoadProgressPct }" />
            </div>
            <output class="loadingValue" aria-live="polite">{{ modelLoadProgressPct }}</output>
          </div>
        </div>
        <ImockupLightingPresets />
        <ImockupFloatingToolbar />
      </div>
    </div>
    <!-- <div class="meta">
      <div v-if="selectedTemplate" class="pill">
        <span class="strong">{{ selectedTemplate.name }}</span>
        <span class="dim">{{ selectedTemplate.width }} × {{ selectedTemplate.height }}</span>
      </div>
      <div class="pill">
        <span class="dim">Scene</span>
        <span class="strong mono">{{ sceneWidth }} × {{ sceneHeight }}</span>
      </div>
    </div> -->
  </section>
</template>

<style scoped>
.stage {
  min-width: 0;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  padding: 0.65rem;
}

.canvasFit {
  flex: 1;
  min-height: 0;
  width: 100%;
  /** 为 `cqw` / `cqh` 提供尺寸参照，供子级 `min(100cqw, …)` 计算 letterbox */
  container-type: size;
  display: flex;
  align-items: center;
  justify-content: center;
  contain: layout style;
}

/**
 * 子级视口与工具条均为 `position:absolute`，不参与文档流高度；
 * 必须用 `width` + `aspect-ratio`（由内联 style 绑定）给出占位尺寸，否则盒子会塌成 0×0。
 */
.canvas {
  position: relative;
  box-sizing: border-box;
  min-width: 0;
  min-height: 0;
  flex-shrink: 1;
  border: 1px solid var(--im-border);
  border-radius: var(--im-radius-lg);
  background: #ffffff;
  overflow: hidden;
  box-shadow: var(--im-shadow);
}

.loadingOverlay {
  position: absolute;
  inset: 0;
  z-index: 9;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgb(17 24 39 / 35%);
  backdrop-filter: blur(2px);
}

.loadingCard {
  width: min(320px, 76%);
  padding: 0.8rem 0.9rem;
  border: 1px solid rgb(255 255 255 / 38%);
  border-radius: 10px;
  background: rgb(15 23 42 / 82%);
  box-shadow: 0 10px 26px rgb(2 6 23 / 36%);
}

.loadingTitle {
  margin: 0 0 0.55rem;
  color: #e5e7eb;
  font-size: 0.82rem;
  font-weight: 700;
}

.loadingBarWrap {
  height: 8px;
  border-radius: 999px;
  background: rgb(148 163 184 / 30%);
  overflow: hidden;
}

.loadingBar {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #60a5fa, #22d3ee);
  transition: width 0.16s ease;
}

.loadingValue {
  margin: 0.5rem 0 0;
  text-align: right;
  color: #e2e8f0;
  font-size: 0.76rem;
  font-variant-numeric: tabular-nums;
}

.meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
  align-items: center;
  justify-content: center;
}

.pill {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.35rem 0.6rem;
  border-radius: 999px;
  border: 1px solid var(--im-border);
  background: #fff;
  font-size: 0.8125rem;
}

.strong {
  font-weight: 850;
  color: #111;
}

.dim {
  color: var(--im-muted);
}

.mono {
  font-variant-numeric: tabular-nums;
}
</style>
